// src/pages/admin/articles/ArticleAdminView.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Menu,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishedIcon,
  FileCopy as DuplicateIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import API from "../../../../../API";
import { showSuccessToast, showErrorToast } from "../../../../../Utils/toastNotifications";

// NEW: import the editor dialog that converts plain -> HTML on save
import ArticleEditorDialog from "./formdialog";

const ArticleAdminView = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Table/filter state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterCategory, setFilterCategory] = useState("");

  // Menus/dialogs
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitialData, setEditorInitialData] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch articles
  const { data: articlesData, isLoading: articlesLoading } = useQuery(
    ["adminArticles", page, rowsPerPage, searchQuery, filterStatus, filterCategory],
    () =>
      API.articles.admin.list({
        page: page + 1,
        per_page: rowsPerPage,
        search: searchQuery || undefined,
        status: filterStatus !== "ALL" ? filterStatus : undefined,
        category: filterCategory || undefined,
      }),
    { keepPreviousData: true, staleTime: 30000 }
  );

  // Fetch categories
  const { data: categoriesData } = useQuery(
    "adminArticleCategories",
    () => API.articles.admin.listCategories(),
    { staleTime: 300000 }
  );

  const articles = articlesData?.data?.articles || [];
  const pagination = articlesData?.data?.pagination || {};
  const categories = categoriesData?.data?.categories || [];

  // Mutations
  const createMutation = useMutation((data) => API.articles.admin.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries("adminArticles");
      setEditorOpen(false);
      setEditorInitialData({});
      showSuccessToast("Article created successfully!");
    },
    onError: (error) => {
      showErrorToast(error?.response?.data?.error || "Failed to create article");
    },
  });

  const updateMutation = useMutation(({ id, data }) => API.articles.admin.update(id, data), {
    onSuccess: () => {
      queryClient.invalidateQueries("adminArticles");
      setEditorOpen(false);
      setEditorInitialData({});
      showSuccessToast("Article updated successfully!");
    },
    onError: (error) => {
      showErrorToast(error?.response?.data?.error || "Failed to update article");
    },
  });

  const deleteMutation = useMutation((id) => API.articles.admin.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries("adminArticles");
      setDeleteDialogOpen(false);
      setSelectedArticle(null);
      showSuccessToast("Article deleted successfully!");
    },
    onError: (error) => {
      showErrorToast(error?.response?.data?.error || "Failed to delete article");
    },
  });

  const publishMutation = useMutation((id) => API.articles.admin.publish(id), {
    onSuccess: () => {
      queryClient.invalidateQueries("adminArticles");
      showSuccessToast("Article published successfully!");
    },
    onError: (error) => {
      showErrorToast(error?.response?.data?.error || "Failed to publish article");
    },
  });

  const unpublishMutation = useMutation((id) => API.articles.admin.unpublish(id), {
    onSuccess: () => {
      queryClient.invalidateQueries("adminArticles");
      showSuccessToast("Article unpublished successfully!");
    },
    onError: (error) => {
      showErrorToast(error?.response?.data?.error || "Failed to unpublish article");
    },
  });

  const duplicateMutation = useMutation((id) => API.articles.admin.duplicate(id), {
    onSuccess: () => {
      queryClient.invalidateQueries("adminArticles");
      showSuccessToast("Article duplicated successfully!");
    },
    onError: (error) => {
      showErrorToast(error?.response?.data?.error || "Failed to duplicate article");
    },
  });

  // Helpers
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      PUBLISHED: "success",
      DRAFT: "default",
      ARCHIVED: "error",
      SCHEDULED: "info",
    };
    return colors[status] || "default";
  };

  // Handlers
  const handleMenuOpen = (event, article) => {
    setAnchorEl(event.currentTarget);
    setSelectedArticle(article);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenCreate = () => {
    setEditorInitialData({
      title: "",
      slug: "",
      subtitle: "",
      excerpt: "",
      content_plain: "",
      category: "",
      tags: [],
      featured_image: "",
      featured_image_alt: "",
      meta_title: "",
      meta_description: "",
      is_featured: false,
      allow_comments: true,
      show_table_of_contents: false,
    });
    setEditorOpen(true);
  };

  const handleOpenEdit = (article) => {
    // Seed editor with existing article (dialog will render preview and convert on save)
    setEditorInitialData({
      id: article.id,
      title: article.title || "",
      slug: article.slug || "",
      subtitle: article.subtitle || "",
      excerpt: article.excerpt || "",
      // prefer plain if you store it; otherwise pass HTML and the dialog will use it as seed
      content: article.content || "",
      category: article.category || "",
      tags: Array.isArray(article.tags) ? article.tags : [],
      featured_image: article.featured_image || "",
      featured_image_alt: article.featured_image_alt || "",
      meta_title: article.meta_title || "",
      meta_description: article.meta_description || "",
      is_featured: !!article.is_featured,
      allow_comments: article.allow_comments !== false,
      show_table_of_contents: !!article.show_table_of_contents,
    });
    setEditorOpen(true);
    handleMenuClose();
  };

  const handleViewArticle = (article) => {
    if (!article) return;
    window.open(`/articles/${article.slug}`, "_blank");
    handleMenuClose();
  };

  const handlePublishToggle = (article) => {
    if (!article) return;
    if (article.status === "PUBLISHED") {
      unpublishMutation.mutate(article.id);
    } else {
      publishMutation.mutate(article.id);
    }
    handleMenuClose();
  };

  const handleDuplicate = (article) => {
    if (!article) return;
    duplicateMutation.mutate(article.id);
    handleMenuClose();
  };

  const handleOpenDelete = (article) => {
    setSelectedArticle(article);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Editor save -> route to create or update
  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const handleEditorSave = (payload) => {
    if (editorInitialData?.id) {
      updateMutation.mutate({ id: editorInitialData.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" fontWeight="bold">
          Article Management
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Create Article
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="PUBLISHED">Published</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                <MenuItem value="ARCHIVED">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.slug}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Articles Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>Author</TableCell>
              <TableCell>Published</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articlesLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">No articles found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => (
                <TableRow key={article.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {article.is_featured && (
                        <Tooltip title="Featured">
                          <StarIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                      <Typography variant="body2" fontWeight={600}>
                        {article.title}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      /{article.slug}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {article.category && (
                      <Chip label={article.category} size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={article.status}
                      size="small"
                      color={getStatusColor(article.status)}
                    />
                  </TableCell>
                  <TableCell>{article.view_count || 0}</TableCell>
                  <TableCell>{article.author?.name || "Unknown"}</TableCell>
                  <TableCell>{formatDate(article.published_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, article)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleViewArticle(selectedArticle)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Article
        </MenuItem>
        <MenuItem onClick={() => handleOpenEdit(selectedArticle)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handlePublishToggle(selectedArticle)}>
          {selectedArticle?.status === "PUBLISHED" ? (
            <>
              <UnpublishedIcon fontSize="small" sx={{ mr: 1 }} />
              Unpublish
            </>
          ) : (
            <>
              <PublishIcon fontSize="small" sx={{ mr: 1 }} />
              Publish
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(selectedArticle)}>
          <DuplicateIcon fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => handleOpenDelete(selectedArticle)}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* NEW: Article Editor Dialog (plain -> HTML) */}
      <ArticleEditorDialog
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditorInitialData({});
        }}
        onSave={handleEditorSave}
        categories={categories}
        initialData={editorInitialData}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedArticle?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedArticle && deleteMutation.mutate(selectedArticle.id)}
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArticleAdminView;
